/**
 * Author-shipped inventory discovery.
 *
 * When a `skaile.manifest.yaml` (adapted to a `version: 2` source config via
 * `manifestToSourceConfig`) carries an `assets:` block, discovery uses the
 * inventory verbatim. No glob walk, no AI enrichment.
 * Per-entry verification:
 *
 *   - Each `files[]` entry exists on disk → hard error otherwise
 *   - The manifest file parses (frontmatter / YAML / JSON) → hard error otherwise
 *   - The kind is registered in the `IAssetKindRegistry` → ghost-install with
 *     a warning (mirrors the Phase 1.7 amendment for unknown kinds)
 *   - SHA256 hash is computed deterministically and compared against
 *     `entry.sha256` when present → hard error on mismatch
 *   - `entry.requires` refs are syntactically valid → hard error otherwise
 *
 * Output shape matches `discoverAssetsInTree()` so downstream consumers
 * (`LocalCatalogSource`, store backend) need no changes.
 */

import * as fs from "node:fs";
import * as path from "node:path";
import { parseFrontmatter } from "@skaile/workspaces/core/manifest";
import type { IAssetKindRegistry } from "@skaile/workspaces/plugins";
import * as yaml from "js-yaml";
import type { DiscoveredAsset, DiscoveryResult } from "./discover.js";
import { computeDeterministicHashDetailed } from "./hash.js";
import type { ReferenceWarning } from "./reference-lint.js";
import { buildRequiresGraph, type RequiresEdge } from "./requires-graph.js";
import type { AssetEntry, SourceConfigV2 } from "./source-config.js";
import { barePublisher, isExcludedDevPath } from "./source-config.js";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Discover assets from an author-shipped inventory.
 *
 * Walks each `assets[]` entry in `sourceConfig`, verifies the declared file
 * filter against disk, parses the manifest, computes the deterministic hash,
 * validates requires syntax, and emits a `DiscoveredAsset` per entry.
 *
 * Verification failures are recorded in `result.errors` as
 * `<error-tag>: <relative-path>: <detail>` and the offending entry is skipped.
 * Tags: `ManifestFileMissing`, `ManifestSchemaError`, `ManifestHashMismatch`,
 * `ManifestRequiresError`.
 *
 * @param rootPath - Absolute path to the repo root
 * @param sourceConfig - Validated `version: 2` source config with `assets:` populated
 * @param registry - Asset kind registry for kind validation + file-filter fallback
 * @returns A {@link DiscoveryResult} identical in shape to `discoverAssetsInTree()`
 * @docLink packages/discovery/concepts#discover-from-manifest
 */
export function discoverFromManifest(
  rootPath: string,
  sourceConfig: SourceConfigV2,
  registry: IAssetKindRegistry,
  includeDev = false,
): DiscoveryResult {
  const assets: DiscoveredAsset[] = [];
  const errors: Array<{ path: string; error: string }> = [];
  const warnings: ReferenceWarning[] = [];
  const allEdges: RequiresEdge[] = [];
  const devPaths = sourceConfig.dev_paths ?? [];

  // Stable ordering: sort by `path` so output is deterministic regardless
  // of how the author chose to list entries.
  const sortedEntries = [...sourceConfig.assets].sort((a, b) => (a.path < b.path ? -1 : 1));

  for (const entry of sortedEntries) {
    // Dev-only assets are excluded from a normal run; --dev re-includes them.
    if (!includeDev && isExcludedDevPath(entry.path, devPaths)) continue;

    const result = processEntry(rootPath, entry, registry);
    warnings.push(...result.warnings);
    if (result.errors.length > 0) {
      errors.push(...result.errors);
      if (!result.asset) continue;
    }
    if (result.asset) {
      assets.push(result.asset);
      allEdges.push(...result.asset.requires);
    }
  }

  const graph = buildRequiresGraph(allEdges);
  // Author-shipped inventory declares its own bytes explicitly — no structural
  // filter to second-guess, so the atomization lint does not apply here.
  return { assets, graph, errors, warnings };
}

// ---------------------------------------------------------------------------
// Per-entry processing
// ---------------------------------------------------------------------------

interface ProcessEntryResult {
  asset: DiscoveredAsset | null;
  errors: Array<{ path: string; error: string }>;
  warnings: ReferenceWarning[];
}

function processEntry(
  rootPath: string,
  entry: AssetEntry,
  registry: IAssetKindRegistry,
): ProcessEntryResult {
  const errors: Array<{ path: string; error: string }> = [];
  const warnings: ReferenceWarning[] = [];

  // ── 1. Resolve asset root + manifest path ─────────────────────────────────
  const assetRoot = path.resolve(rootPath, entry.path);
  const assetRootRelative = entry.path;

  // A `root`-only entry (no explicit `files`) is materialised by walking the
  // subtree — mirrors the `skaile.manifest.yaml` `root:` form.
  const files = entry.files ?? expandRootFiles(assetRoot);
  if (entry.files === undefined && files.length === 0) {
    errors.push({
      path: assetRootRelative,
      error: `ManifestFileMissing: root "${entry.root ?? entry.path}" contains no files`,
    });
    return { asset: null, errors, warnings };
  }

  // The manifest file is whichever entry in `files[]` looks like the manifest
  // — i.e. matches the registered kind's `matchesPath`. We try each `files[]`
  // entry against the registry; the first match wins. If no entry matches
  // (e.g. unknown kind), fall back to the first file in the list.
  const manifestFileRel = pickManifestFile(entry.path, files, entry.kind, registry);
  const manifestAbsPath = path.join(assetRoot, manifestFileRel);

  // ── 2. Verify every file exists on disk (hard error) ──────────────────────
  const missing: string[] = [];
  for (const file of files) {
    if (!fs.existsSync(path.join(assetRoot, file))) {
      missing.push(file);
    }
  }
  if (missing.length > 0) {
    errors.push({
      path: assetRootRelative,
      error: `ManifestFileMissing: ${missing.join(", ")}`,
    });
    return { asset: null, errors, warnings };
  }

  // ── 3. Parse manifest ─────────────────────────────────────────────────────
  const manifest = parseManifest(manifestAbsPath);
  if (!manifest) {
    errors.push({
      path: assetRootRelative,
      error: `ManifestSchemaError: failed to parse manifest file ${manifestFileRel}`,
    });
    return { asset: null, errors, warnings };
  }

  // ── 4. Resolve kind via registry (ghost-install on unknown) ───────────────
  if (!registry.getProvider(entry.kind)) {
    warnings.push({
      path: assetRootRelative,
      warning: `Unknown asset kind "${entry.kind}" — ghost-installing without provider validation`,
    });
  }

  // ── 5. Validate `requires` syntax ─────────────────────────────────────────
  const requiresErrors = validateRequiresSyntax(entry.requires);
  if (requiresErrors.length > 0) {
    errors.push({
      path: assetRootRelative,
      error: `ManifestRequiresError: ${requiresErrors.join("; ")}`,
    });
    return { asset: null, errors, warnings };
  }

  // ── 6. Compute hash + compare against entry.sha256 ────────────────────────
  const { sha256: computedHash, missing: hashMissing } = computeDeterministicHashDetailed(
    assetRoot,
    files,
  );

  if (hashMissing.length > 0) {
    // existsSync passed at step 2 but readFileSync failed — race / perms.
    errors.push({
      path: assetRootRelative,
      error: `ManifestFileMissing: ${hashMissing.join(", ")} (became unreadable during hashing)`,
    });
    return { asset: null, errors, warnings };
  }

  if (entry.sha256 && entry.sha256 !== computedHash) {
    errors.push({
      path: assetRootRelative,
      error: `ManifestHashMismatch: declared ${entry.sha256}, computed ${computedHash}`,
    });
    return { asset: null, errors, warnings };
  }

  if (!entry.sha256) {
    // Coded so the sync layer can collapse many of these into one summary line;
    // per-asset computed hash omitted (it added noise, one line per asset).
    warnings.push({
      path: assetRootRelative,
      warning: "Missing sha256 — auto-computed locally (only needed for store publish).",
      code: "missing_sha256",
    });
    // Non-fatal: proceed with the computed hash.
  }

  // ── 7. Build the DiscoveredAsset ──────────────────────────────────────────
  // `entry.publisher` is `@`-prefixed after `adaptManifestAssetEntry`/`atPublisher`,
  // but `DiscoveredAsset.publisher` must be BARE — the `core/src/ref.ts` invariant,
  // mirroring the glob path. A stored `@lark` publisher makes the asset un-addable
  // (`add`/resolution query `lark`, index held `@lark`). The ref keeps exactly one
  // `@` scope sigil (same as the glob path), so glob + manifest ids stay identical.
  const publisher = barePublisher(entry.publisher);
  const ref = `@${publisher}/${entry.name}@${entry.version}`;
  const requires: RequiresEdge[] = entry.requires.map((to) => ({
    from: ref,
    to,
    field: "assets[].requires",
  }));

  // Merge entry.metadata into the parsed manifest so AssetDefinition.manifest
  // carries the author's passthrough fields downstream. Manifest fields win
  // on key collision — the author already declared identity in the entry.
  const mergedManifest: Record<string, unknown> = {
    ...(entry.metadata ?? {}),
    ...manifest,
  };

  const asset: DiscoveredAsset = {
    ref,
    kind: entry.kind,
    publisher,
    name: entry.name,
    version: entry.version,
    manifest: mergedManifest,
    manifestPath: manifestAbsPath,
    relativePath: path.join(assetRootRelative, manifestFileRel).replace(/\\/g, "/"),
    fileFilter: {
      assetRoot,
      files: [...files].sort(),
    },
    sha256: computedHash,
    requires,
  };

  return { asset, errors, warnings };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const MANIFEST_FILENAMES = new Set([
  "SKILL.md",
  "AGENT.md",
  "agent.yaml",
  "CONNECTOR.md",
  "MCP.md",
  "CONTRACT.md",
  "PERSONA.md",
  "RULESET.md",
]);

const MANIFEST_SUFFIXES = [".flow.yaml", ".flow.json", ".prompt.md", ".preset.yaml"];

function pickManifestFile(
  assetPath: string,
  files: string[],
  kind: string,
  registry: IAssetKindRegistry,
): string {
  const provider = registry.getProvider(kind);

  // Provider knows best — give it the candidates and ask which matches.
  if (provider) {
    for (const file of files) {
      if (provider.matchesPath(path.join(assetPath, file).replace(/\\/g, "/"))) {
        return file;
      }
    }
  }

  // Fallback to filename-based heuristics so we can still surface a
  // sensible manifestPath when the kind is unknown.
  for (const file of files) {
    if (MANIFEST_FILENAMES.has(path.basename(file))) return file;
    if (MANIFEST_SUFFIXES.some((s) => file.endsWith(s))) return file;
  }

  // Last resort: first file in the list.
  return files[0]!;
}

/**
 * Recursively list every file under `assetRoot`, relative to it (POSIX
 * separators), skipping `.git` / `node_modules` / `.skaile`. Used to
 * materialise a `root`-only manifest entry's file set.
 */
function expandRootFiles(assetRoot: string): string[] {
  const out: string[] = [];
  const walk = (absDir: string, relDir: string): void => {
    let entries: fs.Dirent[];
    try {
      entries = fs.readdirSync(absDir, { withFileTypes: true });
    } catch {
      return;
    }
    for (const e of entries.sort((a, b) => (a.name < b.name ? -1 : 1))) {
      const rel = relDir ? `${relDir}/${e.name}` : e.name;
      if (e.isDirectory()) {
        if (e.name === ".git" || e.name === "node_modules" || e.name === ".skaile") continue;
        walk(path.join(absDir, e.name), rel);
      } else if (e.isFile()) {
        out.push(rel);
      }
    }
  };
  walk(assetRoot, "");
  return out;
}

function parseManifest(absPath: string): Record<string, unknown> | null {
  try {
    const content = fs.readFileSync(absPath, "utf-8");

    if (absPath.endsWith(".md")) {
      const { data } = parseFrontmatter(content);
      // A .md with no `---` block yields `{}`; for a kind-matched file that is a
      // parse failure, not a default-identity manifest.
      if (!data || Object.keys(data).length === 0) return null;
      return data as Record<string, unknown>;
    }

    if (absPath.endsWith(".yaml") || absPath.endsWith(".yml")) {
      const parsed = yaml.load(content);
      if (typeof parsed === "object" && parsed !== null) {
        return parsed as Record<string, unknown>;
      }
      return null;
    }

    if (absPath.endsWith(".json")) {
      return JSON.parse(content) as Record<string, unknown>;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Validate the syntax of a single `requires:` entry.
 *
 * Accepted shapes:
 *   - `<publisher>/<name>` (loose, no version)
 *   - `<publisher>/<name>@<semver-range>` (e.g. `@org/foo@^1`)
 *   - `<publisher>/<name>@<exact-version>`
 *
 * Returns a list of human-readable error strings (empty on success).
 */
function validateRequiresSyntax(requires: string[]): string[] {
  const errors: string[] = [];
  // Loose validator: must start with `@`, contain one `/`, optional `@version`
  const RE = /^@[\w-]+\/[\w.-]+(?:@.+)?$/;
  for (const r of requires) {
    if (!RE.test(r)) errors.push(`invalid requires entry "${r}"`);
  }
  return errors;
}
